// ==========================
// Version 9 — auth/Login.js
// - Sends unverified email/password users to "/verify-email"
// ==========================
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { auth } from '../firebase';
import Seo from '../components/Seo';
import { needsEmailVerification } from './emailVerification';

const NAVY = '#0A1A33';
const GOLD = '#C9A44C';
const IVORY = '#FDFCF9';
const WARM_GRAY = '#888888';
const BURGUNDY = '#5C1A2B';

function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);

    try {
      const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
      if (needsEmailVerification(credential.user)) {
        navigate('/verify-email', { state: { email: credential.user.email } });
        return;
      }
      navigate('/home');
    } catch (err) {
      if (err?.code === 'auth/user-not-found') {
        setError('No account found with this email.');
      } else if (err?.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else {
        setError(err?.message || 'Login failed.');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setBusy(true);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      navigate('/home'); // ✅ redirect to app home
    } catch (err) {
      setError(err?.message || 'Google sign-in failed.');
    } finally {
      setBusy(false);
    }
  };

  const pageUrl = typeof window !== 'undefined' ? `${window.location.origin}/login` : undefined;

  return (
    <div style={styles.page}>
      <Seo
        title="Login • Blessed with a Kiss"
        description="Sign in to keep writing your story and map your memories."
        url={pageUrl}
        type="website"
      />

      <div style={styles.glowTL} />
      <div style={styles.glowBR} />

      <div style={styles.card} aria-label="Login">
        <h2 style={styles.title}>Login</h2>
        <p style={styles.subtitle}>Welcome back. Pick up where your story left off.</p>

        {error ? <div style={styles.error} role="alert">{error}</div> : null}

        <form onSubmit={handleLogin} style={styles.form}>
          <label style={styles.label} htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={styles.input}
            placeholder="you@example.com"
            disabled={busy}
          />

          <label style={styles.label} htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={styles.input}
            placeholder="••••••••"
            disabled={busy}
          />

          <button type="submit" style={styles.primaryBtn} disabled={busy}>
            {busy ? 'Logging in…' : 'Login'}
          </button>
        </form>

        <div style={styles.dividerWrap} aria-hidden="true">
          <div style={styles.dividerLine} />
          <span style={styles.dividerText}>or</span>
          <div style={styles.dividerLine} />
        </div>

        <button onClick={handleGoogleLogin} style={styles.googleBtn} disabled={busy}>
          <img
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
            alt=""
            style={styles.googleIcon}
          />
          Continue with Google
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: 'calc(100dvh - 64px)',
    background: NAVY,
    display: 'grid',
    placeItems: 'center',
    padding: 16,
    position: 'relative',
    overflow: 'hidden',
  },
  glowTL: {
    position: 'absolute',
    top: -120,
    left: -120,
    width: 280,
    height: 280,
    borderRadius: '50%',
    filter: 'blur(40px)',
    opacity: 0.25,
    background: GOLD,
  },
  glowBR: {
    position: 'absolute',
    bottom: -140,
    right: -140,
    width: 340,
    height: 340,
    borderRadius: '50%',
    filter: 'blur(50px)',
    opacity: 0.2,
    background: GOLD,
  },
  card: {
    width: 'min(92vw, 420px)',
    background: IVORY,
    borderRadius: 20,
    padding: 22,
    boxShadow: '0 24px 64px rgba(10,26,51,0.40)',
    border: `1px solid rgba(201,164,76,0.35)`,
    color: NAVY,
  },
  title: {
    margin: 0,
    fontFamily: 'serif',
    fontSize: 26,
  },
  subtitle: {
    margin: '6px 0 14px',
    color: WARM_GRAY,
  },
  error: {
    margin: '8px 0 12px',
    background: 'rgba(92,26,43,0.08)',
    color: BURGUNDY,
    border: '1px solid rgba(92,26,43,0.28)',
    padding: '8px 10px',
    borderRadius: 10,
    fontSize: 14,
  },
  form: {
    display: 'grid',
    gap: 10,
  },
  label: {
    fontWeight: 700,
    marginTop: 6,
  },
  input: {
    padding: 12,
    borderRadius: 12,
    border: `1px solid rgba(10,26,51,0.15)`,
    background: '#fff',
    color: NAVY,
    outline: 'none',
    fontSize: 14,
  },
  primaryBtn: {
    marginTop: 8,
    padding: '12px 14px',
    borderRadius: 12,
    border: 'none',
    background: GOLD,
    color: NAVY,
    cursor: 'pointer',
    fontWeight: 800,
    fontSize: 15,
  },
  dividerWrap: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    gap: 10,
    margin: '16px 0',
  },
  dividerLine: {
    height: 1,
    background: 'rgba(10,26,51,0.15)',
  },
  dividerText: {
    color: WARM_GRAY,
    fontWeight: 700,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  googleBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: 12,
    width: '100%',
    background: '#fff',
    border: `1px solid rgba(10,26,51,0.15)`,
    borderRadius: 12,
    cursor: 'pointer',
    fontWeight: 800,
    fontSize: 14,
    color: NAVY,
  },
  googleIcon: {
    width: 20,
    height: 20,
  },
};

export default Login;
// ==========================
// End Version 9 — auth/Login.js
// ==========================
